import React, { createContext, useContext, useState, useEffect } from 'react';

const ScalyContext = createContext();

export const useScaly = () => {
  const context = useContext(ScalyContext);
  if (!context) {
    throw new Error('useScaly must be used within ScalyProvider');
  }
  return context;
};

export const ScalyProvider = ({ children }) => {
  const [enabled, setEnabled] = useState(() => {
    const saved = localStorage.getItem('scaly_enabled');
    return saved !== null ? saved === 'true' : true;
  });

  const [animationEnabled, setAnimationEnabled] = useState(() => {
    const saved = localStorage.getItem('scaly_animation');
    return saved !== null ? saved === 'true' : true;
  });

  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    const saved = localStorage.getItem('scaly_voice');
    return saved !== null ? saved === 'true' : false; // Voice off by default for MVP
  });

  const [currentAnimation, setCurrentAnimation] = useState('idle');
  const [speechText, setSpeechText] = useState('');
  const [showSpeech, setShowSpeech] = useState(false);

  // Persist settings
  useEffect(() => {
    localStorage.setItem('scaly_enabled', enabled);
  }, [enabled]);

  useEffect(() => {
    localStorage.setItem('scaly_animation', animationEnabled);
  }, [animationEnabled]);

  useEffect(() => {
    localStorage.setItem('scaly_voice', voiceEnabled);
  }, [voiceEnabled]);

  // Animation state machine
  const playAnimation = (animationType, duration = 2000) => {
    if (!animationEnabled) return;
    setCurrentAnimation(animationType);
    setTimeout(() => {
      setCurrentAnimation('idle');
    }, duration);
  };

  // Speech bubble
  const speak = (text, duration = 5000) => {
    if (!enabled) return;
    setSpeechText(text);
    setShowSpeech(true);
    if (duration > 0) {
      setTimeout(() => {
        setShowSpeech(false);
      }, duration);
    }
  };

  const closeSpeech = () => {
    setShowSpeech(false);
  };

  // Event handlers
  const trigger = async (eventType, data = {}) => {
    if (!enabled) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scaly/event`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          event_type: eventType,
          ...data
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.animation) {
          playAnimation(result.animation, result.animation_duration || 2000);
        }
        if (result.speech) {
          speak(result.speech, result.speech_duration || 5000);
        }
      }
    } catch (error) {
      console.error('Scaly event error:', error);
    }
  };

  const value = {
    enabled,
    setEnabled,
    animationEnabled,
    setAnimationEnabled,
    voiceEnabled,
    setVoiceEnabled,
    currentAnimation,
    playAnimation,
    speechText,
    showSpeech,
    speak,
    closeSpeech,
    trigger
  };

  return (
    <ScalyContext.Provider value={value}>
      {children}
    </ScalyContext.Provider>
  );
};
